import { Auth } from 'aws-amplify';
import ArkivetLinkButton from 'components/common/ArkivetLinkButton';
import BoligImages from 'components/common/BoligImages';
import Breadcrumbs from 'components/common/Breadcrumbs';
import LinkButton from 'components/common/LinkButton';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import { connect, MapStateToProps } from 'react-redux';
import { AppState } from 'store';
import { ResponsiveImage, TGCount } from 'store/commonTypes';
import { doFetchFavorites, doAddFavorite, doRemoveFavorite } from 'store/favorites/actions';
import {
  getAddress,
  getBoligtype,
  getByggear, getShowLivingCost,
  getShowMarkedLink,
  getSize,
  makeGetImages,
  makeGetTgCounts,
} from 'store/tilstandsrapportEntities/tilstandsrapport/selectors';
import { doAddVisited } from 'store/visited/actions';
import PropertyDataBanner from './components/PropertyDataBanner';

import styles from './styles.scss';

interface Props {
  tilstandsrapportId: string;
}

interface StateProps {
  tgCount: TGCount;
  boligtype: string;
  size: number;
  byggear: number;
  images?: ResponsiveImage[];
  address: string;
  showLivingCost: boolean;
  showMarkedLink: boolean;
  favorites: any;
}

interface DispatchProps {
  doFetchFavorites: Function;
  doAddFavorite: Function;
  doRemoveFavorite: Function;
  doAddVisited: Function;
}

const ReportOverview = ({
  tgCount,
  boligtype,
  byggear,
  size,
  images,
  tilstandsrapportId,
  address,
  showLivingCost,
  showMarkedLink,
  favorites,
  doFetchFavorites: fetchFavorites,
  doAddFavorite: addFavorite,
  doRemoveFavorite: removeFavorite,
  doAddVisited: addVisited,
}: Props & StateProps & DispatchProps): JSX.Element => {
  const router = useRouter();

  const handleClickFavourite = (b) => async (): Promise<void> => {
    try {
      await Auth.currentAuthenticatedUser();
      if (b) {
        await removeFavorite(tilstandsrapportId);
        fetchFavorites();
      }

      await addFavorite(tilstandsrapportId);
      fetchFavorites();
    } catch (err) {
      router.push(`/signin?redirect=${encodeURI(router.asPath)}`);
    }
  };

  useEffect(() => {
    addVisited(tilstandsrapportId);
    fetchFavorites();
  }, []);

  const isFavourite = favorites.data && favorites.data.map((f) => f.tilstandsRapportId).includes(tilstandsrapportId);

  return (
    <div className={styles.reportOverview}>
      <div className={styles.breadcrumbsContainer}>
        <Breadcrumbs links={[{ href: `/${tilstandsrapportId}`, title: address }]} />
      </div>

      <BoligImages images={images} />

      <div className={styles.container} data-testid="property-status">
        <div className={styles.wrapper}>

          <PropertyDataBanner
            isFavourite={isFavourite}
            propertyData={{
              tilstandsrapportId,
              boligtype,
              size,
              byggear,
              address,
              tgCount,
            }}
            onClickFavourite={handleClickFavourite}
          />

          <div className={styles.linkButtons}>
            <LinkButton
              href={`/list?tilstandsrapportId=${tilstandsrapportId}`}
              as={`/${tilstandsrapportId}/utbedringsplan`}
              text="Se boligens tilstand"
            />
            {showLivingCost && (
              <LinkButton
                href={`/cost?tilstandsrapportId=${tilstandsrapportId}`}
                as={`/${tilstandsrapportId}/bokost`}
                text="Se hva det koster å bo her"
                marginTop={true}
              />
            )}

            <ArkivetLinkButton tilstandsrapportId={tilstandsrapportId} />

            {showMarkedLink && (
              <LinkButton
                href={`https://marked.vendu.no/finans/?id=${tilstandsrapportId}`}
                as={`https://marked.vendu.no/finans/?id=${tilstandsrapportId}`}
                text="Få tilbud på lån!"
                marginTop={true}
                external={true}
              />
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

const makeMapStateToProps = (): MapStateToProps<StateProps, Props, AppState> => {
  const getTgCounts = makeGetTgCounts();
  const getImages = makeGetImages();

  return (state: AppState, ownProps: Props): StateProps => ({
    tgCount: getTgCounts(state, ownProps.tilstandsrapportId),
    boligtype: getBoligtype(state, ownProps.tilstandsrapportId),
    size: getSize(state, ownProps.tilstandsrapportId),
    byggear: getByggear(state, ownProps.tilstandsrapportId),
    images: getImages(state, ownProps.tilstandsrapportId),
    address: getAddress(state, ownProps.tilstandsrapportId),
    showMarkedLink: getShowMarkedLink(state, ownProps.tilstandsrapportId),
    showLivingCost: getShowLivingCost(state, ownProps.tilstandsrapportId),
    favorites: state.favorites,
  });
};

export default connect<StateProps, DispatchProps>(makeMapStateToProps, {
  doFetchFavorites,
  doAddFavorite,
  doRemoveFavorite,
  doAddVisited,
})(ReportOverview);
